import * as api from '@owl-nest/api-client/latest'
import * as models from '@owl-nest/models'
import * as services from '@owl-nest/services'
import type { RequestFailure, ApiResponse, RequestSuccess } from '@owl-nest/api-client'
import type { PromiseAction } from '@owl-nest/redux-wrapper'
import * as Enum from '@owl-nest/legacy-enum'

import type { RootState } from '../reducers'

export const ACTION_TYPE = Enum.create({
  LIKE_PROJECT_FAILURE: 'LIKE_PROJECT_FAILURE',
  LIKE_PROJECT_REQUEST: 'LIKE_PROJECT_REQUEST',
  LIKE_PROJECT_SUCCESS: 'LIKE_PROJECT_SUCCESS',

  UNLIKE_PROJECT_FAILURE: 'UNLIKE_PROJECT_FAILURE',
  UNLIKE_PROJECT_REQUEST: 'UNLIKE_PROJECT_REQUEST',
  UNLIKE_PROJECT_SUCCESS: 'UNLIKE_PROJECT_SUCCESS',

  UPDATE_REQUEST: 'PROJECT.UPDATE_REQUEST',
  UPDATE_FAILURE: 'PROJECT.UPDATE_FAILURE',
  UPDATE_SUCCESS: 'PROJECT.UPDATE_SUCCESS',

  COMPUTE_EXAMPLES_REQUEST: 'PROJECT.COMPUTE_EXAMPLES_REQUEST',
  COMPUTE_EXAMPLES_FAILURE: 'PROJECT.COMPUTE_EXAMPLES_FAILURE',
  COMPUTE_EXAMPLES_SUCCESS: 'PROJECT.COMPUTE_EXAMPLES_SUCCESS',

  SUBMIT_REQUEST: 'PROJECT.SUBMIT_REQUEST',
  SUBMIT_FAILURE: 'PROJECT.SUBMIT_FAILURE',
  SUBMIT_SUCCESS: 'PROJECT.SUBMIT_SUCCESS',

  LOAD_REQUEST: 'PROJECT.LOAD_REQUEST',
  LOAD_FAILURE: 'PROJECT.LOAD_FAILURE',
  LOAD_SUCCESS: 'PROJECT.LOAD_SUCCESS',
})

type LikeProjectFailure = { type: typeof ACTION_TYPE.LIKE_PROJECT_FAILURE }
type LikeProjectRequest = { type: typeof ACTION_TYPE.LIKE_PROJECT_REQUEST }
type LikeProjectSuccess = { type: typeof ACTION_TYPE.LIKE_PROJECT_SUCCESS }

export type ProjectAction =
  | LikeProjectFailure
  | LikeProjectRequest
  | LikeProjectSuccess
  | UpdateRequest
  | UpdateFailure
  | UpdateSuccess
  | ComputeExampleRequest
  | ComputeExampleFailure
  | ComputeExampleSuccess
  | SubmitRequest
  | SubmitFailure
  | SubmitSuccess
  | LoadRequest
  | LoadFailure
  | LoadSuccess

export function likeProject(projectId: number): PromiseAction<RootState, RequestSuccess<void>> {
  return {
    types: [ACTION_TYPE.LIKE_PROJECT_REQUEST, ACTION_TYPE.LIKE_PROJECT_SUCCESS, ACTION_TYPE.LIKE_PROJECT_FAILURE],
    promise: async () => {
      const response = await api.api.post.likeProject({
        urlParams: { projectId: String(projectId) },
      })

      // TODO: HACK: Temporary, until we discuss a better behaviour with Product
      return response.doThrow()
    },
  }
}

// TODO: FIXME: any
export function unlikeProject(projectId: number): any {
  return {
    types: [ACTION_TYPE.UNLIKE_PROJECT_REQUEST, ACTION_TYPE.UNLIKE_PROJECT_SUCCESS, ACTION_TYPE.UNLIKE_PROJECT_FAILURE],
    promise: async () => {
      const response = await api.api.post.unlikeProject({
        urlParams: { projectId: String(projectId) },
      })

      // TODO: HACK: Temporary, until we discuss a better behaviour with Product
      return response.doThrow()
    },
  }
}

type UpdateRequest = { type: typeof ACTION_TYPE.UPDATE_REQUEST }
type UpdateFailure = { type: typeof ACTION_TYPE.UPDATE_FAILURE; failure: RequestFailure }
type UpdateSuccess = { type: typeof ACTION_TYPE.UPDATE_SUCCESS; success: models.project.ProjectForOwner }

export function update(
  project: models.project.ProjectForOwner,
  update: models.project.UpdateProject,
): PromiseAction<ApiResponse<models.project.ProjectForOwner>> {
  return {
    types: [ACTION_TYPE.UPDATE_REQUEST, ACTION_TYPE.UPDATE_SUCCESS, ACTION_TYPE.UPDATE_FAILURE],
    promise: async () => {
      return services.project.update(project.id, update, { extraFields: models.project.OWNER_EXTRA_FIELDS })
    },
  }
}

type ComputeExampleRequest = { type: typeof ACTION_TYPE.COMPUTE_EXAMPLES_REQUEST }
type ComputeExampleFailure = { type: typeof ACTION_TYPE.COMPUTE_EXAMPLES_FAILURE; failure: RequestFailure }
type ComputeExampleSuccess = {
  type: typeof ACTION_TYPE.COMPUTE_EXAMPLES_SUCCESS
  success: api.ProjectExamplesResponse | undefined
}

export function computeExamples(
  project: api.Project,
  config: api.ProjectExamplesRequest,
): PromiseAction<ApiResponse<api.ProjectExamplesResponse | undefined>> {
  return {
    types: [
      ACTION_TYPE.COMPUTE_EXAMPLES_REQUEST,
      ACTION_TYPE.COMPUTE_EXAMPLES_SUCCESS,
      ACTION_TYPE.COMPUTE_EXAMPLES_FAILURE,
    ],
    promise: async () => {
      return services.project.computeExamples(project.id, config)
    },
  }
}

type SubmitRequest = { type: typeof ACTION_TYPE.SUBMIT_REQUEST }
type SubmitFailure = { type: typeof ACTION_TYPE.SUBMIT_FAILURE; failure: RequestFailure }
type SubmitSuccess = { type: typeof ACTION_TYPE.SUBMIT_SUCCESS; success: models.project.ProjectForOwner }

export function submit(
  project: models.project.ProjectForOwner,
  config: api.ProjectSubmitRequest,
): PromiseAction<RootState, ApiResponse<models.project.ProjectForOwner>> {
  return {
    types: [ACTION_TYPE.SUBMIT_REQUEST, ACTION_TYPE.SUBMIT_SUCCESS, ACTION_TYPE.SUBMIT_FAILURE],
    promise: async (_, state) => {
      const result = await services.project.submit(project.id, config)
      // FIXME: if `POST /projects/:id/submit` returned the project, this second call
      // would not be needed
      return result.nextAsync<models.project.ProjectForOwner>(async () => {
        return await services.project.load(project.slug, {
          withToken: state.user.identity.type === 'loggedin',
          extraFields: models.project.OWNER_EXTRA_FIELDS,
        })
      })
    },
  }
}

type LoadRequest = { type: typeof ACTION_TYPE.LOAD_REQUEST }
type LoadFailure = { type: typeof ACTION_TYPE.LOAD_FAILURE; failure: RequestFailure }
type LoadSuccess = { type: typeof ACTION_TYPE.LOAD_SUCCESS; success: models.project.ProjectForOwner }

export function load(slug: string): PromiseAction<RootState, ApiResponse<models.project.ProjectForOwner>> {
  return {
    types: [ACTION_TYPE.LOAD_REQUEST, ACTION_TYPE.LOAD_SUCCESS, ACTION_TYPE.LOAD_FAILURE],
    promise: async (_, state) => {
      return await services.project.load(slug, {
        withToken: state.user.identity.type === 'loggedin',
        extraFields: models.project.OWNER_EXTRA_FIELDS,
      })
    },
  }
}
